import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import EnquiryForm from "@/components/sections/EnquiryForm";
import { Eyebrow } from "@/components/primitives";
import GrowthRing from "@/components/visuals/GrowthRing";
import { Reveal } from "@/components/motion/Reveal";
import { brand } from "@/content/site";
import { Mail, ShieldCheck, Clock, MessageSquare } from "lucide-react";

const points = [
  { icon: MessageSquare, text: "Tell us how your business operates today and where you want it to go." },
  { icon: Clock, text: "We review the information before your initial Growth Assessment." },
  { icon: ShieldCheck, text: "Your details are used only to respond to your enquiry." },
];

export default function Contact() {
  return (
    <>
      <SEO
        title="Book a Free Growth Assessment | Green Circle Group"
        description="Tell Green Circle Group about your trade business, growth goals and current challenges and request a free initial Growth Assessment."
        path="/contact"
      />

      <section className="relative bg-forest pt-28 md:pt-36 pb-20 md:pb-28 overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-40 top-20 opacity-30"
        >
          <GrowthRing size={480} colorFrom="#2CD59C" colorTo="#8DFE3F" duration={90} />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <div className="mb-8">
            <Breadcrumbs
              items={[
                { label: "Home", to: "/" },
                { label: "Contact", to: "/contact" },
              ]}
            />
          </div>

          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
            {/* Left copy */}
            <div className="lg:col-span-5 lg:sticky lg:top-28">
              <Eyebrow dark>Free Growth Assessment</Eyebrow>
              <h1 className="mt-5 text-4xl md:text-5xl font-extrabold tracking-tight leading-[1.06] text-white text-balance">
                Let&apos;s look at what is slowing your growth down.
              </h1>
              <p className="mt-6 text-lg text-white/80 leading-relaxed">
                Tell us about your business, the work you want more of and the
                growth activity that is difficult to keep on top of. We will
                review the information and contact you about an initial Growth
                Assessment.
              </p>

              <ul className="mt-8 space-y-4">
                {points.map((p, i) => {
                  const Icon = p.icon;
                  return (
                    <li key={i} className="flex items-start gap-3">
                      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/10 text-lime">
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="text-white/80">{p.text}</span>
                    </li>
                  );
                })}
              </ul>

              <a
                href={`mailto:${brand.email}`}
                data-testid="contact-email-link"
                className="mt-8 inline-flex items-center gap-2 text-mint font-semibold hover:text-lime transition-colors"
              >
                <Mail className="h-4 w-4" />
                {brand.email}
              </a>
            </div>

            {/* Form card */}
            <div className="lg:col-span-7">
              <Reveal className="rounded-[2rem] bg-white p-6 md:p-9 shadow-[0_30px_80px_rgba(0,0,0,0.25)]">
                <EnquiryForm />
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
